import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Chip,
  Fade,
} from '@mui/material';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FolderIcon from '@mui/icons-material/Folder';
import SchoolIcon from '@mui/icons-material/School';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import useGetDocuments from '../../api/documents/useGetDocuments';
import { keyframes } from '@emotion/react';

// Animazione "pulse" stile AgentStart
const pulseBorder = keyframes`
  0% {
    box-shadow: 0 0 0 4px rgba(223,70,52,0.5);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(223,70,52,0.2);
  }
  100% {
    box-shadow: 0 0 0 4px rgba(223,70,52,0.5);
  }
`;

// Animazione per il menu
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MessageInput = ({
  newMessage,
  setNewMessage,
  handleSendMessage,
  files,
  setFiles,
  inputRef,
  // Nuova prop: se true, mostriamo il testo animato e bordo pulsante
  tutorialFocusOnInput = false,
  // Nuova prop per evidenziare il selettore della classe
  tutorialFocusOnClass = false,
  // Nuove props per il profilo
  profile,
  profileLoading,
  // Nuova prop per il messaggio di aiuto personalizzato
  inputHelpMessage = ''
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const { documents, loading } = useGetDocuments();

  const [anchorEl, setAnchorEl] = useState(null);
  const [showDocumentList, setShowDocumentList] = useState(false);
  
  // Per il menu di selezione classe
  const [classMenuOpen, setClassMenuOpen] = useState(false);
  const classSelectorRef = useRef(null);
  
  // Nuovo stato per tracciare se l'utente ha già cliccato sull'input
  const [hasClickedInput, setHasClickedInput] = useState(false);
  
  const [selectedClass, setSelectedClass] = useState(""); // Classe selezionata attualmente
  const [availableClasses, setAvailableClasses] = useState([]); // Array di classi disponibili

  // Funzione che restituisce l'elenco delle classi in base al tipo di scuola
  const getClassesBySchoolType = (schoolType) => {
    if (!schoolType) return [];
    
    switch (schoolType.toLowerCase()) {
      case 'infanzia':
        return ['prima', 'seconda', 'terza'];
      case 'primaria':
        return ['prima', 'seconda', 'terza', 'quarta', 'quinta'];
      case 'secondaria di primo grado':
      case 'media':
      case 'secondaria i grado':
        return ['prima', 'seconda', 'terza'];
      default:
        return [];
    }
  };

  // Effetto per caricare la classe selezionata dal localStorage al caricamento del componente
  useEffect(() => {
    const savedClass = localStorage.getItem('selectedClass');
    if (savedClass) {
      setSelectedClass(savedClass);
    }
  }, []);
  
  // Effetto per aggiornare le classi disponibili quando cambia il profilo
  useEffect(() => {
    if (profile && !profileLoading) {
      // Determina il tipo di scuola e genera tutte le classi disponibili
      const schoolType = profile.school || "";
      const allClassesForSchool = getClassesBySchoolType(schoolType);
      
      // Se abbiamo classi disponibili per questo tipo di scuola
      if (allClassesForSchool.length > 0) {
        setAvailableClasses(allClassesForSchool);
        
        // Se non è stata selezionata alcuna classe, prova a usare quella dal profilo o la prima disponibile
        if (!selectedClass) {
          // Estrai le classi dal profilo e rimuovi eventuali spazi alla fine
          const profileClasses = profile.current_class_type
            ? profile.current_class_type.split(',').map(c => c.trim()).filter(Boolean)
            : [];
          
          // Trova la prima classe dal profilo che è valida per questo tipo di scuola
          const validProfileClass = profileClasses.find(cls => 
            allClassesForSchool.includes(cls.toLowerCase())
          );
          
          // Usa la classe dal profilo se valida, altrimenti usa la prima disponibile
          const classToSelect = validProfileClass || allClassesForSchool[0];
          setSelectedClass(classToSelect);
          localStorage.setItem('selectedClass', classToSelect);
        }
      }
    }
  }, [profile, profileLoading, selectedClass]);

  // Gestisce il cambio della classe selezionata
  const handleClassChange = (newClass) => {
    setSelectedClass(newClass);
    localStorage.setItem('selectedClass', newClass);
    setClassMenuOpen(false);
    
    // Se siamo nella fase di tutorial per la selezione della classe,
    // passiamo alla fase successiva (focus sull'input)
    if (tutorialFocusOnClass) {
      // Tentativo di accedere ai metodi del componente genitore tramite props
      // In un'implementazione reale dovresti passare una callback come prop
      // ad es. onClassSelected() che viene chiamata qui
      if (window.tutorialHandleClassSelected) {
        window.tutorialHandleClassSelected();
      }
    }
  };

  // Apre/chiude il menu di selezione classe
  const toggleClassMenu = () => {
    setClassMenuOpen(prev => !prev);
  };

  // Click all'esterno per chiudere il menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classSelectorRef.current && !classSelectorRef.current.contains(event.target)) {
        setClassMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Estraggo informazioni dal profilo
  const getProfileInfo = () => {
    if (!profile || profileLoading) return null;
    
    const scuola = profile.school || "";
    let classe = selectedClass || ""; // Usiamo la classe selezionata
    
    if (scuola && classe) {
      return { scuola, classe };
    }
    
    return null;
  };
  
  const profileInfo = getProfileInfo();

  // Apertura menù allegati
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Selezione da "Profilo" (documenti salvati)
  const handleSelectFromProfile = () => {
    setShowDocumentList(true);
    handleClose();
  };

  // Selezione file da input
  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  // Drag & Drop
  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
  };
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Rimuovi file
  const handleRemoveFile = (fileName) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  // Icone in base al file
  const getFileIcon = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    switch (fileExtension) {
      case 'pdf':
        return <PictureAsPdfIcon color="error" />;
      case 'doc':
      case 'docx':
        return <InsertDriveFileIcon style={{ color: '#3F51B5' }} />;
      case 'txt':
        return <InsertDriveFileIcon style={{ color: '#4CAF50' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <ImageIcon style={{ color: '#FF9800' }} />;
      default:
        return <AttachFileIcon />;
    }
  };

  // Selezione documento dal profilo
  const handleSelectDocument = async (doc) => {
    try {
      const response = await fetch(doc.file_url);
      if (!response.ok) {
        throw new Error('Errore nel fetch del file dal profilo');
      }
      const blob = await response.blob();

      // Estensione
      const extensionMatch = doc.document_name.match(/\.[0-9a-z]+$/i);
      const extension = extensionMatch ? extensionMatch[0] : '';
      let fileName = doc.document_name;
      if (!extension) {
        const mimeToExtension = {
          'application/pdf': '.pdf',
          'application/msword': '.doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
          'text/plain': '.txt',
        };
        const inferredExtension = mimeToExtension[blob.type] || '';
        fileName = `${doc.document_name}${inferredExtension}`;
      }

      const file = new File([blob], fileName, {
        type: blob.type,
        lastModified: Date.now(),
      });
      setFiles((prevFiles) => [...prevFiles, file]);

      setShowDocumentList(false);
    } catch (error) {
      console.error('Errore nel caricamento file dal profilo:', error);
      alert('Errore nel caricamento del file dal profilo. Riprova più tardi.');
    }
  };

  // Selezione da camera (mobile)
  const handleCameraSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  // Messaggio introduttivo generale che appare prima del click
  const introMessage = "Cliccando qui puoi chiedere a Minerva di creare quello che desideri. Parlale come se fosse un collega esperto: più dettagli fornisci sul tuo obiettivo didattico, sulla classe e sugli argomenti, più personalizzata e utile sarà la sua risposta!";

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSendMessage}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 0,
          backgroundColor: '#FFFFFF',
          borderRadius: '1.875rem',
          boxShadow: '0px 0.25rem 0.25rem rgba(0, 0, 0, 0.25)',
          width: isMobile ? '100%' : 'calc(100% - 8.75rem)',
          maxWidth: '100%',
          margin: '0 auto',
          marginTop: '0.65rem',
          position: 'relative',
          zIndex: 1,
          // Per abilitare drop
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {/* Tutorial messaggio per focus input */}
        {tutorialFocusOnInput && (
          <Typography 
            sx={{
              position: 'absolute',
              top: 'auto',
              bottom: '80px',  // Posiziono dal basso anziché dall'alto 
              left: '50%',
              transform: 'translateX(-50%)',
              width: '95%',    // Allargo leggermente rispetto al precedente 90%
              maxWidth: '600px', // Imposto una maxWidth più larga
              backgroundColor: 'rgba(255,255,255,0.95)',
              color: '#DF4634',
              fontWeight: 'bold',
              padding: '1rem',
              borderRadius: '12px',
              textAlign: 'center',
              zIndex: 100,
              fontSize: '0.9rem',  // Rendo il testo leggermente più piccolo
              lineHeight: 1.4,     // Migliora la leggibilità
              boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
              animation: 'bounceText 1.5s infinite',
              '@keyframes bounceText': {
                '0%, 100%': { transform: 'translateX(-50%)' },
                '50%': { transform: 'translateX(-50%) translateY(-5px)' },
              },
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: '15px',
                height: '15px',
                backgroundColor: 'white',
                boxShadow: '4px 4px 5px rgba(0,0,0,0.05)',
                zIndex: -1,
              }
            }}
          >
            {hasClickedInput ? inputHelpMessage : introMessage}
          </Typography>
        )}

        {/* NUOVO SELETTORE DI CLASSE */}
        {profileInfo && (
          <Box 
            ref={classSelectorRef}
            sx={{ 
              position: 'absolute', 
              top: isMobile ? '-2.5rem' : '-2.5rem', 
              right: isMobile ? '0.5rem' : '0rem',
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            {/* Se tutorialFocusOnClass => testo animato sopra il selettore classe */}
            {tutorialFocusOnClass && !classMenuOpen && (
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: '3.5rem',
                  right: '10px',
                  width: isMobile ? '180px' : '250px',
                  textAlign: 'right',
                  color: '#DF4634',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  padding: '10px',
                  borderRadius: '12px',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
                  animation: 'bounceText 1.5s infinite',
                  zIndex: 55,
                  '@keyframes bounceText': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-5px)' },
                  },
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    right: '20px',
                    width: '15px',
                    height: '15px',
                    backgroundColor: 'white',
                    transform: 'rotate(45deg)',
                    boxShadow: '4px 4px 5px rgba(0,0,0,0.05)',
                    zIndex: -1,
                  }
                }}
              >
                Seleziona la classe corretta per personalizzare i contenuti per i tuoi studenti. Clicca qui!
              </Typography>
            )}

            {/* Pill button */}
            <Button
              onClick={toggleClassMenu}
              startIcon={<SchoolIcon />}
              endIcon={<KeyboardArrowDownIcon 
                sx={{
                  transform: classMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              />}
              sx={{
                backgroundColor: classMenuOpen ? '#c13c2b' : '#DF4634',
                color: 'white',
                fontWeight: 600,
                fontSize: isMobile ? '0.65rem' : '0.75rem',
                padding: isMobile ? '2px 10px' : '4px 14px',
                height: isMobile ? '26px' : '32px',
                minWidth: 'auto',
                borderRadius: '20px',
                textTransform: 'none',
                boxShadow: classMenuOpen ? '0 4px 6px rgba(0,0,0,0.25)' : '0 2px 4px rgba(0,0,0,0.2)',
                transition: 'all 0.2s ease',
                border: 'none',
                // Se tutorialFocusOnClass => animazione stile "pulse" sul pulsante
                ...(tutorialFocusOnClass
                  ? {
                      animation: `${pulseBorder} 1.5s infinite`,
                      boxShadow: '0 0 0 4px rgba(223,70,52,0.5)',
                    }
                  : {}),
                '&:hover': {
                  backgroundColor: '#c13c2b',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  transform: 'translateY(-2px)',
                },
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                  '& svg': {
                    fontSize: isMobile ? '0.9rem' : '1.1rem',
                  }
                },
                '& .MuiButton-endIcon': {
                  marginLeft: '2px',
                  '& svg': {
                    fontSize: isMobile ? '0.9rem' : '1.1rem',
                  }
                },
              }}
            >
              {`${profileInfo.scuola} ${profileInfo.classe}`}
            </Button>

            {/* Menu a tendina */}
            {classMenuOpen && (
              <Box
                sx={{
                  mb: 0.5,
                  position: 'absolute',
                  bottom: '100%',
                  right: 0,
                  borderRadius: '15px',
                  boxShadow: '0 -5px 25px rgba(0,0,0,0.15)',
                  overflow: 'hidden',
                  width: isMobile ? '160px' : '220px',
                  backgroundColor: 'white',
                  animation: `${slideDown} 0.2s ease-out forwards`,
                  border: '1px solid rgba(0,0,0,0.08)',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    right: isMobile ? '32px' : '40px',
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'white',
                    transform: 'rotate(45deg)',
                    borderRight: '1px solid rgba(0,0,0,0.08)',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    zIndex: 0,
                  }
                }}
              >
                <Box sx={{ 
                  p: 1.5, 
                  pb: 0.5,
                  borderBottom: '1px solid rgba(0,0,0,0.06)'
                }}>
                  <Typography sx={{ 
                    fontSize: isMobile ? '0.7rem' : '0.8rem', 
                    color: 'rgba(0,0,0,0.6)',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Seleziona classe
                  </Typography>
                </Box>
                
                <Box sx={{ maxHeight: 200, overflowY: 'auto', p: 0.5 }}>
                  {availableClasses.map((classe, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => handleClassChange(classe)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        borderRadius: '10px',
                        m: 0.3,
                        minHeight: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.15s ease',
                        backgroundColor: selectedClass === classe ? 'rgba(223,70,52,0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: selectedClass === classe ? 'rgba(223,70,52,0.12)' : 'rgba(0,0,0,0.04)',
                        }
                      }}
                    >
                      {selectedClass === classe ? (
                        <CheckCircleIcon 
                          sx={{ 
                            fontSize: isMobile ? '0.9rem' : '1.1rem',
                            mr: 1,
                            color: '#DF4634'
                          }}
                        />
                      ) : (
                        <Box sx={{ width: isMobile ? '0.9rem' : '1.1rem', mr: 1 }} />
                      )}
                      <Typography 
                        sx={{ 
                          fontWeight: selectedClass === classe ? 600 : 400,
                          fontSize: isMobile ? '0.75rem' : '0.85rem',
                          color: selectedClass === classe ? '#DF4634' : 'rgba(0,0,0,0.75)',
                        }}
                      >
                        {`${profile?.school || ""} ${classe}`}
                      </Typography>
                    </MenuItem>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Lista file caricati */}
        {files.length > 0 && (
          <Box
            sx={{
              width: '100%',
              padding: '0.625rem',
              overflowY: 'auto',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            {files.map((file, index) => {
              const fileExtension = file.name.split('.').pop().toLowerCase();
              if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
                return (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: '100px',
                      height: '100px',
                      borderRadius: '0.5rem',
                      overflow: 'hidden',
                      border: '1px solid #ccc',
                    }}
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFile(file.name)}
                      sx={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              } else {
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                      justifyContent: 'space-between',
                      backgroundColor: '#f5f5f5',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getFileIcon(file)}
                      <Typography
                        variant="body2"
                        sx={{ marginLeft: '0.5rem', wordBreak: 'break-all' }}
                      >
                        {file.name}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFile(file.name)}
                    >
                      Rimuovi
                    </Button>
                  </Box>
                );
              }
            })}
          </Box>
        )}

        {/* Documenti salvati (dal Profilo) */}
        {showDocumentList && (
          <Box
            sx={{
              width: '100%',
              padding: '0.625rem',
              backgroundColor: '#f5f5f5',
              borderRadius: '1rem',
              position: 'relative',
              marginBottom: '1rem',
            }}
          >
            <IconButton
              sx={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
              onClick={() => setShowDocumentList(false)}
            >
              <CloseIcon />
            </IconButton>

            <Typography
              variant="h6"
              sx={{ marginBottom: '1rem', textAlign: 'center' }}
            >
              Documenti Salvati
            </Typography>

            {loading ? (
              <Typography>Caricamento documenti...</Typography>
            ) : (
              <List>
                {documents.map((doc) => (
                  <ListItem
                    key={doc.id}
                    button
                    onClick={() => handleSelectDocument(doc)}
                  >
                    <ListItemIcon>{getFileIcon({ name: doc.document_name })}</ListItemIcon>
                    <ListItemText primary={doc.document_name} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Box con input e bottone invio */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            // Se tutorialFocusOnInput => animazione stile "pulse" attorno
            ...(tutorialFocusOnInput
              ? {
                  animation: `${pulseBorder} 1.5s infinite`,
                  borderRadius: '2rem',
                  position: 'relative',
                }
              : {}),
          }}
        >
          <IconButton onClick={handleClick}>
            <AttachFileIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{
              '& .MuiPaper-root': {
                width: isMobile ? '50%' : '300px',
                borderRadius: '0.5rem',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              },
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: isMobile ? 'center' : 'left',
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: isMobile ? 'center' : 'left',
            }}
          >
            <MenuItem
              onClick={() => {
                document.getElementById('file-input').click();
                handleClose();
              }}
              sx={{ padding: '1rem' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={{ fontSize: '1rem', color: '#000' }}>
                  Carica File
                </Typography>
                <UploadFileIcon sx={{ fontSize: '1.5rem', color: '#000' }} />
              </Box>
            </MenuItem>

            {isMobile && (
              <MenuItem
                onClick={() => document.getElementById('camera-input').click()}
                sx={{ padding: '1rem' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Typography sx={{ fontSize: '1rem', color: '#000' }}>
                    Scatta una Foto
                  </Typography>
                  <CameraAltIcon sx={{ fontSize: '1.5rem', color: '#000' }} />
                </Box>
              </MenuItem>
            )}

            <MenuItem onClick={handleSelectFromProfile} sx={{ padding: '1rem' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={{ fontSize: '1rem', color: '#000' }}>
                  Seleziona dal Profilo
                </Typography>
                <FolderIcon sx={{ fontSize: '1.5rem', color: '#000' }} />
              </Box>
            </MenuItem>
          </Menu>

          {/* Input per caricare file */}
          <input
            accept=".txt,.pdf,.docx,.doc,.jpg,.jpeg,.png,.gif"
            style={{ display: 'none' }}
            id="file-input"
            multiple
            type="file"
            onChange={handleFileSelect}
          />

          {/* Nuovo input per camera su mobile */}
          {isMobile && (
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="camera-input"
              capture="environment"
              type="file"
              onChange={handleCameraSelect}
            />
          )}

          <TextField
            inputRef={inputRef} // Attach the ref to the input
            fullWidth
            variant="outlined"
            multiline
            minRows={1}
            maxRows={5}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Scrivi un messaggio a MinervaAI..."
            autoComplete="off"
            onClick={() => {
              // Quando l'utente clicca sull'input, impostiamo hasClickedInput a true
              if (!hasClickedInput) {
                setHasClickedInput(true);
              }
            }}
            sx={{
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '1.875rem',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
                '& textarea': {
                  maxHeight: '150px',
                  overflow: 'auto',
                },
              },
            }}
          />

          <Button
            type="submit"
            sx={{
              marginLeft: '0.3rem',
              marginRight: '0.625rem',
              backgroundColor: 'transparent',
              borderRadius: '50%',
              width: '2rem',
              height: '2rem',
              minWidth: '2rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0.5rem',
            }}
          >
            <img
              src="/chat_icon/send_icon.png"
              alt="Invia"
              style={{ 
                width: '1.5rem', 
                height: '1.5rem',
                objectFit: 'contain'
              }}
            />
          </Button>
        </Box>
      </Box>
      <Typography
        sx={{
          color: '#555555',
          fontSize: { xs: '0.5rem', sm: '0.7rem' },
          textAlign: 'center',
          marginTop: '0.5rem',
          marginBottom: '1rem',
          fontStyle: 'italic',
          fontWeight: 500,
        }}
      >
        Potrebbero esserci imprecisioni, il tuo tocco li rende perfetti
      </Typography>
    </>
  );
};

export default MessageInput;
